// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
//
// A comment she pins on the artwork: saves it, then opens the Comments pane
// and selects it once the review carries it.
import { useCallback, useEffect, useState } from "react";

import { useCreateAskPunttReviewComment } from "./useAskPunttReview";
import { type AskV2ReviewLeftPane } from "./useAskV2ReviewWorkspace";

import { type HumanCommentSubmitInput } from "../../components/ask-puntt-review";

type HumanCommentInput = {
  reviewId: string | null | undefined;
  comments: readonly { _id: string }[] | undefined;
  setLeftPane: (pane: AskV2ReviewLeftPane) => void;
  selectComment: (commentId: string) => void;
};

export function useAskV2ReviewHumanComment({
  reviewId,
  comments,
  setLeftPane,
  selectComment,
}: HumanCommentInput) {
  const { mutateAsync: createReviewComment } =
    useCreateAskPunttReviewComment(reviewId);
  const [pendingFocusCommentId, setPendingFocusCommentId] = useState<
    string | null
  >(null);
  const handleSubmitHumanComment = useCallback(
    async (input: HumanCommentSubmitInput) => {
      const { comment } = await createReviewComment({
        description: input.description,
        rel_x: input.relX,
        rel_y: input.relY,
        page_num: input.page,
      });
      setPendingFocusCommentId(comment._id);
      setLeftPane("comments");
    },
    [createReviewComment, setLeftPane],
  );

  // why: the saved comment reaches `review.comments` one render after the
  // mutation resolves; selecting it synchronously misses the focus lookup.
  useEffect(() => {
    if (pendingFocusCommentId == null) return;
    if (!comments?.some((c) => c._id === pendingFocusCommentId)) {
      return;
    }
    selectComment(pendingFocusCommentId);
    setPendingFocusCommentId(null);
  }, [pendingFocusCommentId, comments, selectComment]);

  return handleSubmitHumanComment;
}
